/**
 * 画面の振り分け（画面仕様書 §1）。
 *
 * ロールに合わない画面は一覧から隠すだけでなく、直接 URL を開いた場合も止める。
 * ただし本当の権限判定はサーバー側で行っており、これは誤操作を防ぐための表示制御。
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell.js';
import { Loading, Notice } from './components/ui.js';
import { useAuth } from './auth/context.js';
import type { Role } from './api/types.js';
import { LoginPage } from './pages/LoginPage.js';
import { CalculationPage } from './pages/admin/CalculationPage.js';
import { DashboardPage } from './pages/admin/DashboardPage.js';
import { EmployeesPage } from './pages/admin/EmployeesPage.js';
import { GroupsPage } from './pages/admin/GroupsPage.js';
import { IssuesPage } from './pages/admin/IssuesPage.js';
import { MonthlyPage } from './pages/admin/MonthlyPage.js';
import { ApprovalDetailPage } from './pages/approver/ApprovalDetailPage.js';
import { ApprovalHistoryPage } from './pages/approver/ApprovalHistoryPage.js';
import { ApprovalScopePage } from './pages/approver/ApprovalScopePage.js';
import { ApprovalsPage } from './pages/approver/ApprovalsPage.js';
import { DailyEntryPage } from './pages/staff/DailyEntryPage.js';
import { HomePage } from './pages/staff/HomePage.js';
import { MonthlyEntryPage } from './pages/staff/MonthlyEntryPage.js';
import { MonthlyListPage } from './pages/staff/MonthlyListPage.js';
import { MonthlySummaryPage } from './pages/staff/MonthlySummaryPage.js';

function RequireRole({
  roles,
  children,
}: {
  roles: Role[];
  children: React.ReactNode;
}): React.JSX.Element {
  const { me } = useAuth();
  if (!me) return <Loading />;
  if (!roles.includes(me.employee.role)) {
    return (
      <Notice tone="warn" title="この画面を表示する権限がありません">
        必要な場合は管理部にお問い合わせください。
      </Notice>
    );
  }
  return <>{children}</>;
}

export function App(): React.JSX.Element {
  const { loading, account, me } = useAuth();

  if (loading) return <Loading />;

  // 認証できても利用者マスタにいない場合はログイン画面に留める
  if (!account || !me) return <LoginPage />;

  const approver: Role[] = ['approver', 'admin'];
  const admin: Role[] = ['admin'];

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/daily" element={<DailyEntryPage />} />
        <Route path="/daily/:date" element={<DailyEntryPage />} />
        <Route path="/monthly-entry" element={<MonthlyEntryPage />} />
        <Route path="/monthly-list" element={<MonthlyListPage />} />
        <Route path="/monthly-summary" element={<MonthlySummaryPage />} />

        <Route
          path="/approvals"
          element={
            <RequireRole roles={approver}>
              <ApprovalsPage />
            </RequireRole>
          }
        />
        <Route
          path="/approvals/history"
          element={
            <RequireRole roles={approver}>
              <ApprovalHistoryPage />
            </RequireRole>
          }
        />
        <Route
          path="/approvals/scope"
          element={
            <RequireRole roles={approver}>
              <ApprovalScopePage />
            </RequireRole>
          }
        />
        <Route
          path="/approvals/:reportId"
          element={
            <RequireRole roles={approver}>
              <ApprovalDetailPage />
            </RequireRole>
          }
        />

        <Route
          path="/admin"
          element={
            <RequireRole roles={admin}>
              <DashboardPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/issues"
          element={
            <RequireRole roles={admin}>
              <IssuesPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/monthly"
          element={
            <RequireRole roles={admin}>
              <MonthlyPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/calculation"
          element={
            <RequireRole roles={admin}>
              <CalculationPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/calculation/:employeeId/:date"
          element={
            <RequireRole roles={admin}>
              <CalculationPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/groups"
          element={
            <RequireRole roles={admin}>
              <GroupsPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/employees"
          element={
            <RequireRole roles={admin}>
              <EmployeesPage />
            </RequireRole>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
